import { useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import { useCurrentFolderStore } from './stores/useCurrentFolderStore'
import { useCurrentWorkspace } from './useCurrentWorkspace'
import { CreateMediaFileParams, MediaFileClient } from 'api/clients/MediaFileClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import {
  UploadCompleteDetails,
  uploadCompleteEventName,
} from 'screens/Finder/components/views/GalleryView/events/uploadComplete'
import { MessageType } from 'antd/lib/message'
import { useFinderContext } from 'screens/Finder/FinderContext'
import { upsertFilesProperties } from 'redux/actions/filesProperties'
import { AlmanacFile, SelectedImage } from 'types'
import { AssignedProperty } from 'api/clients/PropertyClient'
import { reportError } from 'utils/error'
import { UPLOAD_MIME_TYPES } from 'screens/Finder/components/views/GalleryView/constants'
import { useCurrentWorkspaceId } from './useCurrentWorkspaceId'
import { useUploadsProject } from './useUploadsProject'
import { useQueryClient } from 'react-query'

export const ONE_GB_IN_BYTES = 1024 * 1024 * 1024 // 1,073,741,824 bytes
export const ONE_MB_IN_BYTES = 1024 * 1024 // 1,048,576 bytes

const formatFileSize = (sizeInBytes: number): string => {
  if (sizeInBytes >= ONE_GB_IN_BYTES) {
    return `${sizeInBytes / ONE_GB_IN_BYTES}GB`
  } else {
    return `${Math.round(sizeInBytes / ONE_MB_IN_BYTES)}MB`
  }
}

export const useImportMediaFiles = (
  dispatchSuccessEvent = false,
  onSuccess?: (files: AlmanacFile[], assignedProperty?: AssignedProperty) => void,
  onUploadProgress?: (progress: number, index?: number) => void,
  options?: {
    forceExtractBackgroundJobSync?: boolean
    maxFileSize?: number
  },
) => {
  const { forceExtractBackgroundJobSync, maxFileSize = ONE_GB_IN_BYTES } = options || {}
  const videoUploadsWithProgress = !!onUploadProgress
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { currentFolder } = useCurrentFolderStore()
  const workspace = useCurrentWorkspace()
  const { quickAddFile } = useFinderContext()
  const dispatch = useDispatch()
  const currentWorkspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()
  const uploadsProjectQuery = useUploadsProject(currentWorkspaceId)
  const uploadsProject = uploadsProjectQuery?.data?.project || null
  const resetFilesQueries = () =>
    queryClient.invalidateQueries(['workspace', currentWorkspaceId, 'files', uploadsProject?.id])
  let dismissToast: MessageType | undefined

  const selectInput = () => {
    fileInputRef.current?.click()
  }

  const reset = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    if (dismissToast) {
      dismissToast()
    }
    setIsUploading(false)
    resetFilesQueries()
  }

  const uploadImagesFromEvent = (
    event: React.ChangeEvent<HTMLInputElement>,
    parentFolderId?: string,
    createParams?: Partial<CreateMediaFileParams>,
    showUploadingToast: boolean = true,
  ) => {
    const files = Array.from(event.target.files || [])

    upload(files, parentFolderId, createParams, showUploadingToast)
  }

  const onSubmit = async (payload?: SelectedImage[], saveInUploadsFolder?: boolean) => {
    if (!payload) {
      return
    }

    const files = await Promise.all(
      payload.map(async (image) => {
        const response = await fetch(image.url)
        const blob = await response.blob()
        return new File([blob], image.original_filename || 'filename', { type: blob.type })
      }),
    )

    // Filter out any undefined or null values just in case
    const validFiles = files.filter(Boolean)
    const folderId = saveInUploadsFolder ? uploadsProject?.id : undefined
    upload(validFiles as File[], folderId)
  }

  const upload = async (
    files: File[],
    parentFolderId?: string,
    params?: Partial<CreateMediaFileParams>,
    showUploadingToast: boolean = true,
  ) => {
    const folderId = parentFolderId || currentFolder?.id || uploadsProject?.id
    const unsupportedFileType = files.find((f) => !UPLOAD_MIME_TYPES.includes(f.type))

    if (unsupportedFileType) {
      showToastMessage(`File type not supported: ${unsupportedFileType.type}`, 'error')
      reset()
      return
    }

    if (files.length === 0 || !folderId || !workspace?.id) {
      reset()

      return
    }

    const hasFileSizeError = maxFileSize ? files.find((file) => file.size > maxFileSize) : false

    if (hasFileSizeError) {
      showToastMessage(`File size is too large, maximum file size is ${formatFileSize(maxFileSize)}`, 'error')
      reset()
      return
    }

    if (showUploadingToast) {
      dismissToast = showToastMessage('Uploading...', 'loading', undefined, 0)
    }

    setIsUploading(true)

    let uploadedFiles: AlmanacFile[] = []
    let hasErrorOccurred = false
    const fileProgress = new Array(files.length).fill(0)

    await Promise.all(
      files.map(async (file: File, index: number) => {
        const signedUrlRes = await MediaFileClient.getSignedUrl(workspace.id, folderId, file.name)

        const errorToastMessage = 'There was a problem uploading your file. Try again.'

        if (signedUrlRes.type !== SUCCESS_RESPONSE_TYPE) {
          showToastMessage(errorToastMessage, 'error')
          reportError('Unable to generate presigned url')
          hasErrorOccurred = true
          return
        }

        const uploadToS3Response = await MediaFileClient.uploadToS3(
          signedUrlRes.data.pre_signed_url,
          file,
          (progress) => {
            fileProgress[index] = progress
            const totalProgress = fileProgress.reduce((acc, curr) => acc + curr, 0) / files.length
            onUploadProgress?.(totalProgress)
          },
        )

        if (!uploadToS3Response.ok) {
          showToastMessage(errorToastMessage, 'error')
          reportError('Unable to upload your media file to S3')
          hasErrorOccurred = true
          return
        }

        const url = uploadToS3Response.url.split('?')[0]
        const createParams = {
          ...params,
          source_url: url,
          mime_type: file.type,
          title: file.name,
          project_key: folderId,
          extract_metadata_in_background_job: forceExtractBackgroundJobSync ? false : videoUploadsWithProgress,
        }

        const createMediaFileRes = await MediaFileClient.create(workspace.id, createParams)

        if (createMediaFileRes.type !== SUCCESS_RESPONSE_TYPE) {
          showToastMessage(errorToastMessage, 'error')
          reportError('Unable to save media file after successful upload')
          hasErrorOccurred = true
          return
        }

        const { almanac_file: uploadedFile, assigned_property: assignedProperty } = createMediaFileRes.data

        uploadedFiles.push(uploadedFile)

        if (dispatchSuccessEvent) {
          document.body.dispatchEvent(
            new CustomEvent<UploadCompleteDetails>(uploadCompleteEventName, {
              bubbles: false,
              detail: { file: uploadedFile, assignedProperty },
            }),
          )
        }

        if (quickAddFile) {
          quickAddFile(uploadedFile)
          if (assignedProperty) {
            dispatch(upsertFilesProperties([assignedProperty]))
          }
        }
      }),
    )

    if (hasErrorOccurred) {
      showToastMessage('Some files could not be uploaded.', 'error')
    } else {
      onSuccess?.(uploadedFiles)
    }

    reset()

    return uploadedFiles
  }

  const uploadImageFilesWithoutLoadingToast = (
    files: File[],
    parentFolderId?: string,
    params?: Partial<CreateMediaFileParams>,
  ) => {
    return upload(files, parentFolderId, params, false)
  }

  return {
    selectInput,
    fileInputRef,
    uploadImageFiles: upload,
    uploadImageFilesWithoutLoadingToast,
    uploadImagesFromEvent,
    isUploading,
    onSubmit,
  }
}
